import { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Center, Divider, Heading, StackDivider, VStack, Link, Text, Icon, HStack, useDisclosure } from '@chakra-ui/react'
import { Paginate } from '@!/components/Paginate'
import { isBlank } from '@!/Util'
import { ListItem, SearchGuidePopupModal, SafeSearchText } from '@!/Store/SearchList/components'
import { SearchForm, TagSearchBox } from '@!/Store/Common/containers'
import { TopTabs, ScrollableLatelyAccessList } from '@!/Store/Common/components'
import { PointBackBanner, CampaignHeaderBanner } from '@!/components/Banner'
import { useAtom } from 'jotai'
import { userHashAtom } from '@!/atoms/userInfoAtom'
import { useCheckPurchasedBooks, useTabs, useSearch } from '@!/Hooks'
import { breadcrumbAtom } from '@!/atoms/breadcrumbAtom'
import { SafeSearch } from '@!/Menu/components/SafeSearch'
import { menuOpenAtom } from '@!/atoms/appSettingsAtom'
import { LiaSlidersHSolid } from 'react-icons/lia'
import { SearchFilterModal } from '@!/Store/SearchList/components/SearchFilterModal'
import { CircularProgressModal } from '@!/components/Modal'

const StoreSearchList = ({ type, searchTerm: keyword, page, tabName, searchTags, metaTag }) => {
  const [, setBreadcrumb] = useAtom(breadcrumbAtom)
  const [userHash] = useAtom(userHashAtom)
  const [bookTitles, setBookTitles] = useState([])
  const contentIds = useMemo(() => bookTitles.map((bookTitle) => bookTitle.first_volume.content_id), [bookTitles])
  const { data: purchasedBookIds, isSuccess: isSuccess } = useCheckPurchasedBooks(userHash, contentIds)
  const { currentTab } = useTabs(tabName)
  const genreName = currentTab.genreName
  const genreLabel = currentTab.genreLabel
  const tabLabel = currentTab.label
  const [isModalOpen, setModalOpen] = useState(false)
  const [, setIsMenuOpen] = useAtom(menuOpenAtom)

  const [currentPage, setCurrentPage] = useState(page)

  const genreMap = {
    man: ['boy', 'man'],
    woman: ['girl', 'lady'],
    tl: ['tl'],
    bl: ['bl'],
  }
  const initialOptions = useMemo(() => {
    if (!tabName) return {}

    return { genre_names: genreMap[tabName] || [] }
  }, [tabName])
  const [options, setOptions] = useState(initialOptions)

  const [totalCount, setTotalCount] = useState(0)
  const [perPage, setPerPage] = useState(1)

  // 検索API
  const {
    data: { titles, total_count, per_page, excluded_titles_present: isExcludedTitlesPresent, campaign_info: campaign } = {},
    isLoading,
    isError,
  } = useSearch(type, keyword, currentPage, options)

  useEffect(() => {
    setBookTitles(titles || [])
    setTotalCount(total_count || 0)
    setPerPage(per_page || 1)
  }, [titles, total_count, per_page])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [bookTitles])

  const openModal = () => setModalOpen(true)
  const closeModal = () => setModalOpen(false)
  const handleMenuOpen = (open) => {
    setIsMenuOpen(open)
  }

  useEffect(() => {
    if (!isSuccess) return

    setBookTitles(
      bookTitles.map((bookTitle) => {
        bookTitle.first_volume.purchased = purchasedBookIds.some((purchasedBookId) => purchasedBookId == bookTitle.first_volume.content_id)
        return bookTitle
      }),
    )
  }, [isSuccess])

  const topText = useMemo(() => {
    switch (type) {
      case 'publisher':
        return `${keyword}の漫画作品を紹介するページです。${keyword}から配信されている少年漫画、青年漫画、少女漫画を紹介しています。新刊も随時配信されます。${keyword}の漫画作品をお探しの方はこちらからお探しください。`
      case 'author':
      case 'magazine':
      case 'label':
        return `${keyword}の漫画作品を紹介するページです。${keyword}の漫画をお探しの方はこちらからお探しください。`
      case 'category':
        return `${keyword}ジャンルの漫画作品を紹介するページです。複数の出版社の${keyword}ジャンルの漫画を紹介しています。${keyword}漫画をお探しの方はこちらからお探しください。`
      default:
        return null
    }
  }, [type, keyword])

  useEffect(() => {
    setBreadcrumb([{ name: 'マンガBANGブックス', ref: '/' }, { name: `検索結果(${genreLabel})` }, { name: keyword }])
  }, [type, keyword, genreLabel])

  const handlePageClick = (page) => {
    setCurrentPage(page)
  }

  const { isOpen: isFilterModalOpen, onOpen: openFilterModal, onClose: closeFilterModal } = useDisclosure()

  const handleFilterSearch = (filters) => {
    const options = {}

    if (filters.selectedDeals?.includes('無料あり')) options.free_flag = true
    if (filters.selectedDeals?.includes('割引あり')) options.discount_flag = true

    if (filters.selectedPublicationStatus?.includes('新刊')) options.new_flag = true
    if (filters.selectedPublicationStatus?.includes('完結')) options.end_flag = true

    if (filters.selectedGenres?.length) options.genre_names = filters.selectedGenres
    if (filters.selectedSubGenres?.length) options.sub_genre_names = filters.selectedSubGenres

    setCurrentPage(1)
    setOptions(options)
  }

  // meta tag 更新
  useEffect(() => {
    const pageText = currentPage > 1 ? `（${currentPage}ページ目）` : ''
    const metaTitle = `「${keyword}」の作品一覧${pageText}｜${metaTag.title}`
    const metaDescription = `${keyword}の作品一覧${pageText}をチェック。${metaTag.description}`
    const firstImageUrl = titles?.[0]?.image_url

    // タイトルを更新（ページ番号反映）
    document.title = metaTitle

    // meta description を更新
    const descriptionTag = document.querySelector('meta[name="description"]')
    if (descriptionTag) {
      descriptionTag.setAttribute('content', metaDescription)
    }

    // og:image を更新（SNS シェア時の画像）
    const ogImageTag = document.querySelector('meta[property="og:image"]')
    if (ogImageTag && firstImageUrl) {
      ogImageTag.setAttribute('content', firstImageUrl)
    }

    // 検索結果がない場合は noindex を追加
    const robotsTag = document.querySelector('meta[name="robots"]')
    if (type === 'keyword' || (titles && titles.length === 0)) {
      if (robotsTag) {
        // 既存の <meta name="robots"> がある場合は content を noindex に変更
        robotsTag.setAttribute('content', 'noindex')
      } else {
        // <meta name="robots"> タグが存在しない場合は新しく作成し noindex を設定
        const newTag = document.createElement('meta')
        newTag.name = 'robots'
        newTag.content = 'noindex'
        document.head.insertBefore(newTag, document.head.firstChild)
      }
    } else if (robotsTag?.content === 'noindex') {
      // 検索結果がある場合は noindex を削除
      robotsTag.remove()
    }
  }, [currentPage, keyword, metaTag, titles, type])

  return (
    <Box>
      <CircularProgressModal isOpen={isLoading} />
      <TopTabs tabName={tabName} />
      {campaign ? <CampaignHeaderBanner campaign={campaign} /> : <PointBackBanner />}
      <Box mt={3}>
        <Box mb={-3}>
          <SearchForm keyword={type === 'keyword' ? keyword : null} tabName={tabName} placeholder={`${genreLabel}｜キーワード（作品、著者など）入力`} />
        </Box>
        <Box mx={4} fontSize="3xs" textAlign="right">
          <Link color="label.primary" textDecoration="none" fontWeight="300" onClick={openModal}>
            検索について ▼
          </Link>
        </Box>
      </Box>
      <SearchGuidePopupModal isOpen={isModalOpen} handleClose={closeModal} />
      <Box>
        <Box px="16px" py="12px">
          <Heading as="h1" fontSize="xs" fontWeight="300" lineHeight="18px" mb={2}>
            {genreLabel}からの検索結果「{keyword}」
          </Heading>
          <Box fontSize="3xs" fontWeight="300" lineHeight="14px">
            {topText}
          </Box>
          <SafeSearchText showWarningText={isExcludedTitlesPresent} handleMenuOpen={handleMenuOpen} />

          <Box w="100%" mt={4} border="solid 1px #B8B8BD" cursor="pointer" onClick={openFilterModal}>
            <HStack spacing={1} w="100%" p={1.5} justifyContent="center">
              <Text fontSize="sm" fontWeight="semibold" lineHeight={1}>
                絞り込み検索
              </Text>
              <Icon as={LiaSlidersHSolid} fontSize="xl" />
            </HStack>
          </Box>
          <SearchFilterModal
            isOpen={isFilterModalOpen}
            onClose={closeFilterModal}
            type={type}
            title={`${genreLabel}からの検索結果`}
            keyword={keyword}
            tabName={tabName}
            genreMap={genreMap}
            onSearch={handleFilterSearch}
          />

          <Box my={4}>
            {/* 表示できる本があるとき */}
            {!isBlank(bookTitles) && (
              <>
                <Divider />
                <VStack spacing={0} divider={<StackDivider />}>
                  {bookTitles.map((bookTitle, index) => (
                    <ListItem key={`store-search-list-item-${index}`} bookTitle={bookTitle} />
                  ))}
                </VStack>
                <Divider />
                <Center mt="24px">
                  <Paginate currentPage={currentPage} perPage={perPage} totalCount={totalCount} onPageChange={handlePageClick} />
                </Center>
              </>
            )}

            {/* エラーまたは検索結果が0かつセーフサーチの除外もされていないとき */}
            {(isError || (!isLoading && isBlank(bookTitles) && !isExcludedTitlesPresent)) && <Box>該当する作品は見つかりませんでした。</Box>}

            {/* セーフサーチによる除外で表示できる本が0のとき */}
            {!isLoading && isBlank(bookTitles) && isExcludedTitlesPresent && (
              <>
                <Box>セーフサーチにより表示できる作品がありません。</Box>
                <Center mt="24px">
                  <Paginate currentPage={currentPage} perPage={perPage} totalCount={totalCount} onPageChange={handlePageClick} />
                </Center>
              </>
            )}
          </Box>
          <Center mb={4}>
            <Link href={currentTab.url} fontSize="xs">
              {genreName ? `${genreLabel}TOP` : tabLabel}に戻る
            </Link>
          </Center>
          <Box py={3} px={4} boxShadow="0px 2px 6px rgba(0, 0, 0, 0.1)">
            <SafeSearch />
          </Box>
        </Box>
        <Divider borderBottomWidth="12px" borderColor="bg.secondary" my={4} />
        <ScrollableLatelyAccessList />
        <Box py={3} px={4}>
          <Divider borderBottomWidth="2px" borderColor="bg.secondary" />
        </Box>
        <TagSearchBox searchTags={searchTags} genreName={genreName} />
      </Box>
    </Box>
  )
}

StoreSearchList.propTypes = {
  type: PropTypes.string,
  q: PropTypes.string,
  result: PropTypes.object,
  searchTags: PropTypes.array.isRequired,
  tabName: PropTypes.string,
}

export default StoreSearchList
